'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from 'react';
import { createPortal } from 'react-dom';
import { VStack, HStack, Box } from '@terra/ui-styled-system';
import {
  DndContext,
  type DragEndEvent,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button } from '../../components/button';
import { IconButton } from '../../components/icon-button';
import { Menu } from '../../components/menu';
import { LabelDefault } from '../../components/typography';
import { RichMarkdownEditor } from '../rich-markdown-editor';
import {
  FileCodeIcon,
  Image01Icon,
  File01Icon,
  DragDropVerticalIcon,
  Add01Icon,
  Delete02Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  POC_IMAGE_MAX_SIZE_BYTES,
  POC_IMAGE_MIME_TYPES,
  POC_MAX_IMAGE_BLOCKS,
  type PocBlock,
  type PocEvidencePhase,
} from '@terra/shared-schemas-orchestrator';
import { findingsClient } from '@terra/shared-rest-clients-findings';
import { toasterApi } from '../toast/ToastProvider';
import { StructuredPocImageBlockEditor } from './StructuredPocImageBlockEditor';
import { EvidenceBlockEditor } from './EvidenceBlockEditor';

export type EvidenceItem = {
  toolExecutionHarId?: string;
  explanation: string;
  whyItMatters?: string;
  phase?: PocEvidencePhase;
};
type StructuredPocEditorProps = {
  blocks: PocBlock[];
  onChange: (blocks: PocBlock[]) => void;
  narrativesOnly?: boolean;
  findingId?: string;
  actionPortalId?: string;
  isDraftDirty?: boolean;
};

export function StructuredPocEditor({
  blocks,
  onChange,
  narrativesOnly,
  findingId,
  actionPortalId,
  isDraftDirty,
}: StructuredPocEditorProps) {
  const t = useTranslations('designSystem.findings.structuredPocEditor');
  const tCommon = useTranslations('designSystem.common');
  const blocksRef = useRef(blocks);
  const addedImageIdsRef = useRef<Set<string>>(new Set());
  const pendingRemovalImageIdsRef = useRef<Set<string>>(new Set());

  blocksRef.current = blocks;
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [actionPortalNode, setActionPortalNode] = useState<HTMLElement | null>(
    null,
  );

  useLayoutEffect(() => {
    if (!actionPortalId) {
      setActionPortalNode(null);

      return;
    }

    setActionPortalNode(document.getElementById(actionPortalId));
  }, [actionPortalId]);
  useEffect(() => {
    if (!isDraftDirty) {
      addedImageIdsRef.current.clear();
      pendingRemovalImageIdsRef.current.clear();
    }
  }, [isDraftDirty]);
  const updateBlock = useCallback(
    (index: number, updated: PocBlock) => {
      const current = blocksRef.current;
      const newBlocks = [...current];

      newBlocks[index] = updated;
      blocksRef.current = newBlocks;
      onChange(newBlocks);
    },
    [onChange],
  );
  const removeBlock = useCallback(
    async (index: number) => {
      const current = blocksRef.current;
      const blockToRemove = current[index];

      if (
        blockToRemove?.type === 'image' &&
        findingId &&
        blockToRemove.imageId
      ) {
        if (addedImageIdsRef.current.has(blockToRemove.imageId)) {
          await findingsClient
            .deleteFindingPocImage({
              params: { id: findingId, imageId: blockToRemove.imageId },
            })
            .then(() => {
              addedImageIdsRef.current.delete(blockToRemove.imageId);
            })
            .catch(() => {
              toasterApi.warning({
                title: t('imageDeleteFailedTitle'),
                description: t('imageDeleteFailedDescription'),
              });
            });
        } else {
          pendingRemovalImageIdsRef.current.add(blockToRemove.imageId);
        }
      }

      const newBlocks = current.filter((_, i) => i !== index);

      blocksRef.current = newBlocks;
      onChange(newBlocks);
    },
    [findingId, onChange, t],
  );
  const freePendingRemovedImages = useCallback(async (): Promise<boolean> => {
    if (!findingId || pendingRemovalImageIdsRef.current.size === 0) {
      return true;
    }

    const imageIdsToDelete = [...pendingRemovalImageIdsRef.current];
    const results = await Promise.allSettled(
      imageIdsToDelete.map((imageId) =>
        findingsClient
          .deleteFindingPocImage({
            params: { id: findingId, imageId },
          })
          .then(() => {
            pendingRemovalImageIdsRef.current.delete(imageId);
          }),
      ),
    );

    if (results.some((result) => result.status === 'rejected')) {
      toasterApi.warning({
        title: t('imageDeleteFailedTitle'),
        description: t('imageDeleteFailedDescription'),
      });

      return false;
    }

    return true;
  }, [findingId, t]);
  const moveBlock = useCallback(
    (fromIndex: number, toIndex: number) => {
      const current = blocksRef.current;

      if (toIndex < 0 || toIndex >= current.length) return;
      const newBlocks = arrayMove(current, fromIndex, toIndex);

      blocksRef.current = newBlocks;
      onChange(newBlocks);
    },
    [onChange],
  );
  const addNarrativeBlock = useCallback(
    (atIndex: number) => {
      const current = blocksRef.current;
      const newBlocks = [...current];

      newBlocks.splice(atIndex, 0, { type: 'narrative', content: '' });
      blocksRef.current = newBlocks;
      onChange(newBlocks);
    },
    [onChange],
  );
  const addEvidenceBlock = useCallback(
    (atIndex: number, evidence: EvidenceItem) => {
      const current = blocksRef.current;
      const newBlocks = [...current];

      newBlocks.splice(atIndex, 0, {
        type: 'evidence',
        evidenceType: 'http',
        toolExecutionHarId: evidence.toolExecutionHarId,
        explanation: evidence.explanation ?? '',
        whyItMatters: evidence.whyItMatters,
        phase: evidence.phase,
      });
      blocksRef.current = newBlocks;
      onChange(newBlocks);
    },
    [onChange],
  );
  const addManualEvidenceBlock = useCallback(
    (atIndex: number) => {
      addEvidenceBlock(atIndex, {
        explanation: '',
        whyItMatters: '',
      });
    },
    [addEvidenceBlock],
  );
  const imageBlocksCount = useMemo(
    () => blocks.filter((block) => block.type === 'image').length,
    [blocks],
  );
  const isSupportedUploadMimeType = useCallback(
    (mimeType: string) =>
      POC_IMAGE_MIME_TYPES.includes(
        mimeType as (typeof POC_IMAGE_MIME_TYPES)[number],
      ),
    [],
  );
  const canAddImage =
    !narrativesOnly &&
    Boolean(findingId) &&
    imageBlocksCount < POC_MAX_IMAGE_BLOCKS;
  const triggerImagePicker = useCallback(() => {
    if (!canAddImage) {
      if (imageBlocksCount >= POC_MAX_IMAGE_BLOCKS) {
        toasterApi.warning({
          title: t('maxImagesReachedTitle'),
          description: t('maxImagesReachedDescription'),
        });
      }

      return;
    }

    imageInputRef.current?.click();
  }, [canAddImage, imageBlocksCount, t]);
  const handleImageInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (!file || !findingId) {
        return;
      }

      if (!isSupportedUploadMimeType(file.type)) {
        toasterApi.warning({
          title: t('imageUploadFailedTitle'),
          description: t('imageUnsupportedTypeDescription'),
        });
        event.target.value = '';

        return;
      }

      if (file.size > POC_IMAGE_MAX_SIZE_BYTES) {
        toasterApi.warning({
          title: t('imageUploadFailedTitle'),
          description: t('imageTooLargeDescription'),
        });
        event.target.value = '';

        return;
      }

      setIsUploadingImage(true);

      try {
        const freedPendingRemovals = await freePendingRemovedImages();

        if (!freedPendingRemovals) {
          return;
        }

        const response = await findingsClient.uploadFindingPocImage({
          params: { id: findingId },
          body: { image: file },
        });

        if (response.status !== 200) {
          throw new Error('Failed to upload finding PoC image');
        }

        const uploadedImage = response.body.image;
        const current = blocksRef.current;
        const newBlocks: PocBlock[] = [
          ...current,
          {
            type: 'image',
            imageId: uploadedImage.imageId,
            mimeType: uploadedImage.mimeType,
            fileName: uploadedImage.fileName,
          },
        ];

        addedImageIdsRef.current.add(uploadedImage.imageId);
        blocksRef.current = newBlocks;
        onChange(newBlocks);
      } catch {
        toasterApi.error({
          title: t('imageUploadFailedTitle'),
          description: t('imageUploadFailedDescription'),
        });
      } finally {
        event.target.value = '';
        setIsUploadingImage(false);
      }
    },
    [
      findingId,
      freePendingRemovedImages,
      isSupportedUploadMimeType,
      onChange,
      t,
    ],
  );
  const sortableIds = useMemo(
    () => blocks.map((_, index) => index),
    // Intentionally [blocks.length] only: stable ids per position avoid remounting blocks on every keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps -- blocks.length is sufficient for sortable identity
    [blocks.length],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 0,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over == null || active.id === over.id) return;
      const oldIndex = sortableIds.indexOf(Number(active.id));
      const newIndex = sortableIds.indexOf(Number(over.id));

      if (oldIndex === -1 || newIndex === -1) return;
      moveBlock(oldIndex, newIndex);
    },
    [sortableIds, moveBlock],
  );
  const listContainerRef = useRef<HTMLDivElement>(null);
  const modifiers = useMemo(
    () => [
      restrictToVerticalAxis,
      (args: Parameters<typeof restrictToParentElement>[0]) =>
        restrictToParentElement({
          ...args,
          containerNodeRect:
            listContainerRef.current?.getBoundingClientRect() ?? null,
        }),
    ],
    [],
  );
  let editorEvidenceCounter = 0;
  const actionMenu = (
    <Menu.MenuWithItems
      positioning={{ strategy: 'fixed' }}
      items={[
        {
          value: 'narrative',
          label: (
            <HStack gap={2}>
              <File01Icon size={14} />
              <LabelDefault as="span">{t('narrative')}</LabelDefault>
            </HStack>
          ),
          onClick: () => addNarrativeBlock(blocksRef.current.length),
        },
        ...(!narrativesOnly
          ? [
              {
                value: 'evidence',
                label: (
                  <HStack gap={2}>
                    <FileCodeIcon size={14} />
                    <LabelDefault as="span">{t('evidence')}</LabelDefault>
                  </HStack>
                ),
                onClick: () => addManualEvidenceBlock(blocksRef.current.length),
              },
            ]
          : []),
        ...(!narrativesOnly
          ? [
              {
                value: 'image',
                label: (
                  <HStack gap={2}>
                    <Image01Icon size={14} />
                    <LabelDefault as="span">{t('image')}</LabelDefault>
                  </HStack>
                ),
                disabled: !canAddImage || isUploadingImage,
                onClick: () => triggerImagePicker(),
              },
            ]
          : []),
      ]}
      trigger={
        <Button size="xs" variant="ghost" loading={isUploadingImage}>
          <Add01Icon size={12} />
          <LabelDefault as="span">{tCommon('add')}</LabelDefault>
        </Button>
      }
    />
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={modifiers}
    >
      <Box ref={listContainerRef} w="full" overflow="hidden">
        <VStack gap={2} w="full" alignItems="stretch">
          {actionPortalNode ? createPortal(actionMenu, actionPortalNode) : null}
          {!actionPortalNode ? (
            <HStack justifyContent="flex-end">{actionMenu}</HStack>
          ) : null}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/png,image/jpeg"
            hidden
            onChange={handleImageInputChange}
          />

          <SortableContext
            items={sortableIds}
            strategy={verticalListSortingStrategy}
          >
            {blocks.map((block, index) => {
              const evidenceIndex =
                block.type === 'evidence' ? editorEvidenceCounter++ : -1;

              return (
                <VStack key={sortableIds[index]} gap={1} alignItems="stretch">
                  <SortableBlock
                    id={sortableIds[index]}
                    block={block}
                    index={index}
                    evidenceIndex={evidenceIndex}
                    findingId={findingId}
                    updateBlock={updateBlock}
                    removeBlock={removeBlock}
                    moveBlock={moveBlock}
                  />
                </VStack>
              );
            })}
          </SortableContext>
        </VStack>
      </Box>
    </DndContext>
  );
}

type SortableBlockProps = {
  id: number;
  block: PocBlock;
  index: number;
  evidenceIndex: number;
  findingId?: string;
  updateBlock: (index: number, updated: PocBlock) => void;
  removeBlock: (index: number) => void;
  moveBlock: (fromIndex: number, toIndex: number) => void;
};

const SortableBlock = memo(function SortableBlock({
  id,
  block,
  index,
  evidenceIndex,
  findingId,
  updateBlock,
  removeBlock,
  moveBlock,
}: SortableBlockProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const [lockHeight, setLockHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (isDragging && nodeRef.current) {
      setLockHeight(nodeRef.current.getBoundingClientRect().height);
    } else {
      setLockHeight(null);
    }
  }, [isDragging]);
  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      nodeRef.current = el;
      setNodeRef(el);
    },
    [setNodeRef],
  );
  const onUpdate = useCallback(
    (updated: PocBlock) => updateBlock(index, updated),
    [updateBlock, index],
  );
  const onRemove = useCallback(() => removeBlock(index), [removeBlock, index]);
  const moveUp = useCallback(
    () => moveBlock(index, index - 1),
    [moveBlock, index],
  );
  const onMoveUp = index > 0 ? moveUp : undefined;
  const handleNarrativeChange = useCallback(
    (value: string | undefined) =>
      onUpdate({ type: 'narrative', content: value ?? '' }),
    [onUpdate],
  );
  const style = {
    transform:
      transform != null
        ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
        : undefined,
    transition,
    width: '100%',
    flexShrink: 0,
    ...(isDragging && lockHeight != null
      ? { height: lockHeight, overflow: 'hidden' as const }
      : {}),
  };

  return (
    <Box
      ref={setRef}
      position="relative"
      style={style}
      opacity={isDragging ? 0.5 : 1}
      w="full"
      minW={0}
    >
      <HStack
        position="absolute"
        top={1}
        right={1}
        gap={0.5}
        zIndex={1}
        alignItems="center"
      >
        <IconButton
          size="xs"
          variant="ghost"
          onClick={onMoveUp}
          aria-label={onMoveUp ? 'Move up' : 'Drag to reorder'}
          cursor="grab"
          _active={{ cursor: 'grabbing' }}
          {...attributes}
          {...listeners}
        >
          <DragDropVerticalIcon size={12} />
        </IconButton>
        <IconButton
          size="xs"
          variant="ghost"
          colorPalette="red"
          onClick={onRemove}
          aria-label="Remove block"
        >
          <Delete02Icon size={12} />
        </IconButton>
      </HStack>

      {block.type === 'narrative' ? (
        <Box pr={16}>
          <RichMarkdownEditor
            value={block.content}
            onChange={handleNarrativeChange}
            placeholder="Write reproduction step narrative..."
          />
        </Box>
      ) : block.type === 'evidence' ? (
        <EvidenceBlockEditor
          block={block}
          index={evidenceIndex}
          onUpdate={onUpdate}
        />
      ) : block.type === 'image' ? (
        <StructuredPocImageBlockEditor
          block={block}
          findingId={findingId}
          onUpdate={onUpdate}
        />
      ) : null}
    </Box>
  );
});
